<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onServerPrefetch, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useDirectoryRouteFacets } from '@/composables/useDirectoryRouteFacets';
import { useDirectoryRouteLocations } from '@/composables/useDirectoryRouteLocations';
import { countryTextMapping } from '@/helpers/SEO/metaInfoHelper';
import AgentResultList from '@/pages/AgentDirectoryPage/AgentResultList.vue';
import HeroBanner from '@/pages/AgentDirectoryPage/HeroBanner.vue';
import { syncAgentDirectoryFromRoute } from '@/pages/AgentDirectoryPage/syncAgentDirectoryFromRoute';
import { useLocale } from '@/router/routeMeta';
import { useAgentProfilesStore } from '@/stores/agentProfiles';
import { LoadState } from '@/stores/types';

const language = useLocale();
const route = useRoute();

const agentProfilesStore = useAgentProfilesStore();
const {
  agents,
  agentsLoadState,
  totalAgentsCount,
  agentListCurrentPage,
  pageCount,
  searchedLocations,
} = storeToRefs(agentProfilesStore);

const currentPage = computed(() => {
  const page = Number.parseInt((route.query.page as string) ?? 1, 10);

  if (Number.isNaN(page) || page < 1) {
    return 1;
  }

  return page;
});

const filterLocations = useDirectoryRouteLocations();
const filterFacets = useDirectoryRouteFacets();

const loadFromRoute = () =>
  syncAgentDirectoryFromRoute({
    geoLocations: filterLocations.value,
    locale: language.value,
    currentPage: currentPage.value,
    facets: filterFacets.value,
  });

onServerPrefetch(loadFromRoute);

watch(
  [currentPage, filterLocations, filterFacets],
  async () => {
    await loadFromRoute();
  },
  { immediate: true },
);

const generateLocationsForSeo = () => {
  return searchedLocations.value && searchedLocations.value.length > 0
    ? searchedLocations.value.map((item) => item.names[language.value]).join(',')
    : countryTextMapping[language.value];
};

// The agent directory is pre-GA: always keep it out of search engine indexes.
useHead({
  meta: [
    { name: 'robots', content: 'noindex, nofollow' }, // Meta robots tag (for HTML)
    { 'http-equiv': 'x-robots-tag', content: 'noindex, nofollow' }, // X-Robots-Tag (for HTTP headers - works better for some crawlers)
  ],
});
</script>

<template>
  <HeroBanner />
  <div class="layout-container">
    <AgentResultList
      :loadingAgents="agentsLoadState === LoadState.LOADING"
      :agentsLoadFailed="agentsLoadState === LoadState.FAILED"
      :agents="agents"
      :totalAgentsCount="totalAgentsCount"
      :agentListCurrentPage="agentListCurrentPage"
      :pageCount="pageCount"
      :language="language"
      :locationsForSeo="generateLocationsForSeo()"
    />
  </div>
</template>
